/**
 * Terrain color-map factory for Giro3D ElevationLayer.
 *
 * Giro3D's ElevationLayer supports `ColorMapMode` for client-side
 * rendering of elevation/slope/aspect data using color ramps. This
 * module provides factory functions for preset ramps and mode switching.
 */

/** Color ramp preset names matching the converter's surface_staging schema. */
export type ColorRampPreset =
  | 'hypsometric'
  | 'plasma'
  | 'rdylgn'
  | 'gray'
  | 'none';

/** Giro3D ColorMapMode enum values (numeric). */
export const ColorMapMode = {
  None: 0,
  Elevation: 1,
  Slope: 2,
  Aspect: 3,
} as const;

export type ColorMapModeValue = (typeof ColorMapMode)[keyof typeof ColorMapMode];

/**
 * Hypsometric color ramp — sea level to mountain peaks.
 * Returns an array of [position, r, g, b] control points.
 */
function hypsometricRamp(): [number, number, number, number][] {
  return [
    [0.0, 0, 97, 71],       // deep green
    [0.1, 41, 171, 64],     // green
    [0.25, 166, 217, 106],  // yellow-green
    [0.4, 255, 255, 191],   // pale yellow
    [0.55, 253, 174, 97],   // orange
    [0.7, 215, 48, 39],     // red
    [0.85, 165, 0, 38],     // dark red
    [1.0, 255, 255, 255],   // snow white
  ];
}

/**
 * Plasma ramp (Matplotlib-style perceptually uniform).
 */
function plasmaRamp(): [number, number, number, number][] {
  return [
    [0.0, 13, 8, 135],
    [0.14, 75, 3, 161],
    [0.29, 126, 3, 168],
    [0.43, 168, 34, 150],
    [0.57, 203, 70, 121],
    [0.71, 229, 107, 93],
    [0.86, 248, 148, 65],
    [1.0, 240, 249, 33],
  ];
}

/**
 * Red-Yellow-Green diverging ramp.
 */
function rdylgnRamp(): [number, number, number, number][] {
  return [
    [0.0, 215, 48, 39],
    [0.25, 252, 141, 89],
    [0.5, 255, 255, 191],
    [0.75, 145, 207, 96],
    [1.0, 26, 152, 80],
  ];
}

/**
 * Grayscale ramp.
 */
function grayRamp(): [number, number, number, number][] {
  return [
    [0.0, 0, 0, 0],
    [1.0, 255, 255, 255],
  ];
}

const RAMP_REGISTRY: Record<ColorRampPreset, () => [number, number, number, number][]> = {
  hypsometric: hypsometricRamp,
  plasma: plasmaRamp,
  rdylgn: rdylgnRamp,
  gray: grayRamp,
  none: () => [],
};

/**
 * Build a Giro3D-compatible color map configuration.
 *
 * @param rampName — preset ramp name
 * @param min — minimum elevation value
 * @param max — maximum elevation value
 * @returns Object suitable for `ElevationLayer.colorMap`
 */
export function createElevationColorMap(
  rampName: ColorRampPreset,
  min: number,
  max: number,
): {
  mode: ColorMapModeValue;
  min: number;
  max: number;
  colors: [number, number, number, number][];
} {
  const colors = (RAMP_REGISTRY[rampName] ?? hypsometricRamp)();
  return {
    mode: ColorMapMode.Elevation,
    min,
    max,
    colors,
  };
}

/**
 * Apply a color map mode to a Giro3D ElevationLayer (or any layer
 * that exposes a `colorMap` property).
 */
export function applyColorMapMode(
  layer: unknown,
  mode: ColorMapModeValue,
  options?: {
    min?: number;
    max?: number;
    ramp?: ColorRampPreset;
  },
): void {
  const l = layer as any;
  if (!l || typeof l !== 'object') return;

  if (mode === ColorMapMode.None) {
    if ('colorMap' in l) {
      l.colorMap = undefined;
    }
    return;
  }

  const ramp = options?.ramp ?? 'hypsometric';
  const colors = (RAMP_REGISTRY[ramp] ?? hypsometricRamp)();

  l.colorMap = {
    mode,
    min: options?.min ?? 0,
    max: options?.max ?? 1000,
    colors,
  };
}

/**
 * Get the list of available color ramp presets.
 */
export function getAvailableRamps(): ColorRampPreset[] {
  return Object.keys(RAMP_REGISTRY) as ColorRampPreset[];
}

/**
 * Map a GeoViewer colour-ramp preset to the corresponding TiTiler
 * `colormap_name` used by the server-side hypsometric drape (flag ON / 3D mesh).
 *
 * The drape's visible colour is produced by TiTiler (PNG colormap), not the
 * client-side ElevationLayer `colorMap`, so swapping the ramp means rebuilding
 * the drape with a different `colormap_name`. `none` returns `null` to signal
 * "no drape" (show the bare mesh / basemap).
 *
 * - `hypsometric` → `terrain`  (TiTiler's built-in green→brown→white ramp)
 * - `plasma`      → `plasma`
 * - `rdylgn`      → `rdylgn`
 * - `gray`        → `gray`     (greyscale)
 * - `none`        → null       (drape removed by the caller)
 */
export function rampToTitilerColormap(ramp: ColorRampPreset): string | null {
  switch (ramp) {
    case 'hypsometric':
      return 'terrain';
    case 'plasma':
      return 'plasma';
    case 'rdylgn':
      return 'rdylgn';
    case 'gray':
      return 'gray';
    case 'none':
      return null;
    default:
      return 'terrain';
  }
}
